import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Users, 
  BarChart3, 
  UserPlus,
  TrendingUp,
  Award,
  Eye,
  Star,
  ArrowLeft,
  Filter,
  Plus
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAllGuardsBaseScorePerformance } from '@/hooks/useBaseScorePerformanceEvaluation';
import { BehaviorTrackingModal } from '@/components/behavior/BehaviorTrackingModal';
import { BehaviorRecordsList } from '@/components/behavior/BehaviorRecordsList';
import { PerformanceEvaluationDetail } from './PerformanceEvaluationDetail';
import { PerformanceBreakdownView } from './PerformanceBreakdownView';
import { ClientEvaluationModal } from './ClientEvaluationModal';
import { useUserRoles } from '@/hooks/useUserRoles';
import { ShiftaskEvaluationModal } from './ShiftaskEvaluationModal';
import { ShiftaskEvaluationFormModal } from './ShiftaskEvaluationFormModal';
import { DistinctionBadge } from './DistinctionBadge';
import { useQueryClient } from '@tanstack/react-query';
import { subDays } from 'date-fns';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTeams } from '@/pages/applications/hooks/useTeams';
import { useSupabaseUser } from '@/hooks/use-supabase-user';

interface TeamMember {
  id: string;
  user_id: string;
  team_id: string;
  profiles: {
    first_name: string;
    last_name: string;
    full_name?: string;
    avatar_url?: string;
  };
  team?: {
    id: string;
    name: string;
    guardian_sites?: Array<{
      id: string;
      name: string;
    }>;
  };
}

const getGradeColor = (grade: string) => {
  switch (grade) {
    case 'A+':
    case 'A':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'B+':
    case 'B':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'C+':
    case 'C':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'D':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'F':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const AdminPerformanceInterface: React.FC = () => {
  const [selectedEmployee, setSelectedEmployee] = useState<TeamMember | null>(null);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState<string | null>(null);
  const [showBehaviorModal, setShowBehaviorModal] = useState(false);
  const [showClientEvaluationModal, setShowClientEvaluationModal] = useState(false);
  const [showShiftaskEvaluationModal, setShowShiftaskEvaluationModal] = useState(false);
  const [showShiftaskEvaluationFormModal, setShowShiftaskEvaluationFormModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEvaluationId, setExpandedEvaluationId] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  
  const { isAdmin, hasRole, isSuperAdmin } = useUserRoles();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const { user } = useSupabaseUser();
  
  // Get admin's accessible teams
  const { data: adminTeams = [] } = useTeams();
  const adminTeamIds = adminTeams.map(team => team.id);
  
  const isClient = hasRole('client');

  // Use admin's accessible teams for filter
  const teams = adminTeams;

  // Clear cache for team members query on mount
  useEffect(() => {
    queryClient.removeQueries({ queryKey: ['admin-team-members'] });
    queryClient.removeQueries({ queryKey: ['admin-team-members-fixed'] });
  }, [queryClient]);

  // Fetch all team members (employees) - only active ones, distinct to avoid duplicates
  const { data: teamMembers = [], isLoading: membersLoading, error: membersError } = useQuery({
    queryKey: ['admin-team-members-fixed', selectedTeam, adminTeamIds, user?.id],
    enabled: !!user?.id, // Only run when user is loaded
    retry: 1,
    queryFn: async (): Promise<TeamMember[]> => {
      const filteredMembers: any[] = [];
      
      // Super admins see all guards
      if (isSuperAdmin) {
        console.log('Super admin - fetching all team members');
        const query = supabase
          .from('team_members')
          .select(`
            id,
            user_id,
            team_id,
            profiles:profiles!user_id(
              first_name,
              last_name,
              full_name,
              avatar_url,
              status
            ),
            team:teams!team_id(
              id,
              name,
              guardian_sites!team_id(
                id,
                name
              )
            )
          `)
          .neq('profiles.status', 'archived')
          .order('profiles(first_name)', { ascending: true });

        if (selectedTeam !== 'all') {
          query.eq('team_id', selectedTeam);
        }

        const { data, error } = await query;
        if (error) throw error;
        
        for (const member of data || []) {
          if (!member?.profiles?.first_name || !member?.profiles?.last_name) continue;
          
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', member.user_id)
            .maybeSingle();

          if (roleData?.role === 'guard') {
            filteredMembers.push(member);
          }
        }
        
        // Also fetch guards not in any team for super admin
        const { data: allGuards } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, full_name, avatar_url, status')
          .neq('status', 'archived');
        
        if (allGuards) {
          for (const guard of allGuards) {
            const { data: roleData } = await supabase
              .from('user_roles')
              .select('role')
              .eq('user_id', guard.id)
              .maybeSingle();
            
            if (roleData?.role === 'guard') {
              const exists = filteredMembers.some(m => m.user_id === guard.id);
              if (!exists) {
                filteredMembers.push({
                  id: `standalone-${guard.id}`,
                  user_id: guard.id,
                  team_id: '',
                  profiles: {
                    first_name: guard.first_name,
                    last_name: guard.last_name,
                    full_name: guard.full_name,
                    avatar_url: guard.avatar_url,
                    status: guard.status
                  }
                } as TeamMember);
              }
            }
          }
        }
        
        const uniqueMembers = filteredMembers.filter((member, index, self) => 
          index === self.findIndex(m => m.user_id === member.user_id)
        );
        console.log('Super admin found members:', uniqueMembers.length);
        return uniqueMembers;
      }
      
      // Filter by selected team or all admin's teams
      const teamIdsToQuery = selectedTeam !== 'all' ? [selectedTeam] : adminTeamIds;
      
      // Fetch team members if admin has teams
      if (teamIdsToQuery.length > 0) {
      
      let query = supabase
        .from('team_members')
        .select(`
          id,
          user_id,
          team_id,
          profiles:profiles!user_id(
            first_name,
            last_name,
            full_name,
            avatar_url,
            status
          ),
          team:teams!team_id(
            id,
            name,
            guardian_sites!team_id(
              id,
              name
            )
          )
        `)
        .in('team_id', teamIdsToQuery)
        .neq('profiles.status', 'archived')
        .order('profiles(first_name)', { ascending: true });

        const { data, error } = await query;

        if (error) throw error;
        
        // Filter to include only guards
        
        for (const member of data || []) {
        if (!member?.profiles?.first_name || !member?.profiles?.last_name) continue;
        
        // Get role from user_roles table
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', member.user_id)
          .maybeSingle();

        const userRole = roleData?.role;
        
        // Only include users who have the 'guard' role
        const isGuard = userRole === 'guard';

          if (isGuard) {
            filteredMembers.push(member);
          }
        }
      }
      
      // Also fetch guards created by this admin (not in teams)
      const { data: createdGuards, error: createdError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name, avatar_url, status')
        .eq('created_by', user?.id)
        .neq('status', 'archived');
      
      // Add created guards to the list
      if (createdGuards && createdGuards.length > 0) {
        for (const guard of createdGuards) {
          // Check if guard has guard role
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', guard.id)
            .maybeSingle();
          
          if (roleData?.role === 'guard') {
            // Check if not already in filteredMembers
            const exists = filteredMembers.some(m => m.user_id === guard.id);
            if (!exists) {
              filteredMembers.push({
                id: `created-${guard.id}`,
                user_id: guard.id,
                team_id: '',
                profiles: {
                  first_name: guard.first_name,
                  last_name: guard.last_name,
                  full_name: guard.full_name,
                  avatar_url: guard.avatar_url,
                  status: guard.status
                }
              } as TeamMember);
            }
          }
        }
      }
      
      const uniqueMembers = filteredMembers.filter((member, index, self) => 
        index === self.findIndex(m => m.user_id === member.user_id)
      );
      
      console.log(`Total unique members found: ${uniqueMembers.length}`);

      // Filter by selected team if not "all"
      if (selectedTeam !== 'all') {
        const filtered = uniqueMembers.filter(member => 
          member.team_id === selectedTeam
        );
        return filtered;
      }
      
      return uniqueMembers;
    },
  });
  

  // Fetch all performance evaluations με το νέο σύστημα
  const { 
    data: allEvaluations = [], 
    isLoading: evaluationsLoading,
    refetch: refetchEvaluations 
  } = useAllGuardsBaseScorePerformance(
    undefined, // όλες οι ομάδες
    undefined, // τελευταίες 30 ημέρες (default)
    undefined,
    false // Disable real-time updates
  );

  // Fetch OVIT evaluations for stats (within admin bubble)
  const { data: ovitEvaluations = [], error: ovitError } = useQuery({
    queryKey: ['admin-ovit-evaluations', user?.id, isSuperAdmin],
    enabled: !!user,
    queryFn: async () => {
      // First, try a simple count query
      const { count, error: countError } = await supabase
        .from('ovit_evaluations')
        .select('*', { count: 'exact', head: true });

      // Now try to get all evaluations
      const { data, error } = await supabase
        .from('ovit_evaluations')
        .select('*')
        .order('evaluation_date', { ascending: false });

      if (error) throw error;
      return data || [];
    }
  });

  const filteredMembers = teamMembers.filter((member) => {
    if (!searchTerm) return true;
    const fullName = member.profiles.full_name || 
      `${member.profiles.first_name} ${member.profiles.last_name}`;
    return fullName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const selectedEmployeeEvaluations = selectedEmployee 
    ? allEvaluations.filter(evaluation => evaluation.guard_id === selectedEmployee.user_id)
    : [];

  const handleEmployeeSelect = (employee: TeamMember) => {
    setSelectedEmployee(employee);
    setSelectedEvaluationId(null);
  };

  const handleAddBehavior = (employee: TeamMember) => {
    setSelectedEmployee(employee);
    setShowBehaviorModal(true);
  };

  const handleAddClientEvaluation = (employee: TeamMember) => {
    setSelectedEmployee(employee);
    setShowClientEvaluationModal(true);
  };

  const handleViewEvaluation = (evaluationId: string) => {
    setSelectedEvaluationId(evaluationId);
  };

  // Calculate stats using only the LAST performance evaluation for each guard
  // Use allEvaluations (full performance data) instead of ovitEvaluations (just OVIT scores)
  const latestPerformanceByGuard = allEvaluations.reduce((acc: Record<string, any>, evaluation: any) => {
    const guardId = evaluation.guard_id;
    if (!acc[guardId]) {
      acc[guardId] = evaluation;
    }
    return acc;
  }, {});
  
  const latestPerformanceEvaluations = Object.values(latestPerformanceByGuard) as any[];
  
  // Calculate average from final_score (total performance score), not overall_score (OVIT only)
  const totalFinalScore: number = latestPerformanceEvaluations.reduce((sum: number, evaluation: any) => {
    return sum + (Number(evaluation.final_score) || 0);
  }, 0);
  const guardsWithPerformance: number = latestPerformanceEvaluations.length;
  
  // Count unique guards who have Shiftask evaluations
  const uniqueGuardsWithEvaluations = new Set(
    ovitEvaluations.map((evaluation: any) => evaluation.guard_id)
  ).size;
  
  // Find the top performer (guard with highest final_score)
  const topPerformer = latestPerformanceEvaluations.reduce((top: any, current: any) => {
    const currentScore = Number(current.final_score) || 0;
    const topScore = top ? (Number(top.final_score) || 0) : 0;
    return currentScore > topScore ? current : top;
  }, null);
  
  // Get the guard's name from teamMembers
  const topPerformerMember = topPerformer 
    ? teamMembers.find(m => m.user_id === topPerformer.guard_id)
    : null;
  
  const topPerformerName = topPerformerMember?.profiles?.full_name || 
    (topPerformerMember?.profiles ? 
      `${topPerformerMember.profiles.first_name} ${topPerformerMember.profiles.last_name}` : 
      null);
  
  const stats = {
    totalEmployees: teamMembers.length,
    totalEvaluations: uniqueGuardsWithEvaluations, // Count of guards with evaluations, not total evaluation records
    averageScore: guardsWithPerformance > 0 ? totalFinalScore / guardsWithPerformance : 0,
    topPerformerName: topPerformerName,
    topPerformerScore: topPerformer ? Number(topPerformer.final_score) : 0,
    topPerformersCount: latestPerformanceEvaluations.filter((evaluation: any) => {
      const score = Number(evaluation.final_score) || 0;
      return score >= 90; // A+ (95+) and A (90-94)
    }).length,
  };

  if (selectedEvaluationId) {
    return (
      <div className="space-y-6">
        <Button 
          variant="ghost" 
          onClick={() => setSelectedEvaluationId(null)}
          className="mb-4"
        >
          ← {t("backToEvaluations") || "Επιστροφή στις αξιολογήσεις"}
        </Button>
        <PerformanceEvaluationDetail evaluationId={selectedEvaluationId} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Centered Header Row */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-foreground">{t("performanceManagement") || "Διαχείριση Αξιολογήσεων Προσωπικού"}</h1>
        <p className="text-muted-foreground mt-2">
          {t("newBaseScoreSystem") || "Νέο Σύστημα Βασικού Σκορ - Αναλυτική προβολή και διαχείριση αξιολογήσεων"}
        </p>
      </div>

      {/* Controls Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Controls will go here */}
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {(isAdmin || hasRole('manager')) && (
            <>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setShowShiftaskEvaluationFormModal(true)}
                  disabled={!selectedEmployee}
                  variant="default"
                >
                  <Award className="h-4 w-4 mr-2" />
                  {t("shiftaskEvaluation") || "Shiftask Evaluation"}
                </Button>
                <Button 
                  onClick={() => setShowShiftaskEvaluationModal(true)}
                  disabled={!selectedEmployee}
                  variant="outline"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {t("viewHistory") || "View History"}
                </Button>
              </div>
              
              <Button 
                onClick={() => setShowClientEvaluationModal(true)}
                disabled={!selectedEmployee}
                variant="outline"
              >
                <Star className="h-4 w-4 mr-2" />
                {t("clientEvaluation") || "Client Evaluation"}
              </Button>
              
              <Button
                onClick={() => setShowBehaviorModal(true)}
                disabled={!selectedEmployee}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                <Plus className="h-4 w-4" />
                {t("newBehavior") || "New Behavior"}
              </Button>
            </>
          )}
          
          {isClient && !hasRole('manager') && !isAdmin && (
            <Button 
              onClick={() => setShowClientEvaluationModal(true)}
              disabled={!selectedEmployee}
              variant="outline"
            >
              <Star className="h-4 w-4 mr-2" />
              Αξιολόγηση Πελάτη
            </Button>
          )}
          
          <Button 
            variant="ghost" 
            onClick={() => window.location.href = '/dashboard'}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Επιστροφή στο Dashboard
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("totalEmployees") || "Συνολικοί Υπάλληλοι"}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalEmployees}</div>
            <p className="text-xs text-muted-foreground">{t("activeEmployees") || "Ενεργοί υπάλληλοι"}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("evaluations") || "Αξιολογήσεις"}</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalEvaluations}</div>
            <p className="text-xs text-muted-foreground">{t("newSystem") || "Νέο σύστημα"}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("average") || "Μέσος Όρος"}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.averageScore.toFixed(1)}</div>
            <p className="text-xs text-muted-foreground">{t("averageScoreNewSystem") || "Μέσο σκορ (νέο σύστημα)"}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("topPerformer") || "Top Performer"}</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {stats.topPerformerName ? (
              <>
                <div className="text-lg font-bold text-green-600 truncate" title={stats.topPerformerName}>
                  {stats.topPerformerName}
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats.topPerformerScore.toFixed(1)} {t("points") || "points"} • {stats.topPerformersCount} {t("withAGrade") || "with A+ or A"}
                </p>
              </>
            ) : (
              <>
                <div className="text-2xl font-bold text-muted-foreground">-</div>
                <p className="text-xs text-muted-foreground">{t("noEvaluations") || "No evaluations"}</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Employee Selection Panel */}
        <Card className="lg:col-span-1 h-[calc(100vh-300px)] flex flex-col">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t("selectEmployee") || "Επιλογή Υπαλλήλου"}
            </CardTitle>
            <CardDescription>
              {t("selectEmployeeToViewEvaluations") || "Επιλέξτε υπάλληλο για προβολή αξιολογήσεων"}
            </CardDescription>
            {/* Team Filter */}
            <div className="flex items-center gap-2 pt-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("selectTeam") || "Select Team"} />
                </SelectTrigger>
                <SelectContent className="bg-background max-h-[40vh] overflow-y-auto z-50">
                  <SelectItem value="all">{t("allTeams") || "All Teams"}</SelectItem>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col flex-1 min-h-0">
            <div className="space-y-4 flex flex-col flex-1 min-h-0">
              <input
                type="text"
                placeholder={t("searchEmployee") || "Search employee..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-md flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-ring"
              />
              
              <div className="flex-1 overflow-y-auto min-h-0">
                <div className="space-y-2 pr-2">
                  {membersLoading || evaluationsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  ) : filteredMembers.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">{t("noEmployeesFound") || "No employees found"}</p>
                   ) : (
                       filteredMembers.map((member) => {
                        if (!member?.profiles?.first_name || !member?.profiles?.last_name) {
                          return null;
                        }
                        
                        const fullName = member.profiles.full_name || 
                          `${member.profiles.first_name} ${member.profiles.last_name}`;
                        const initials = `${member.profiles.first_name[0] || ''}${member.profiles.last_name[0] || ''}`;
                        const isSelected = selectedEmployee?.id === member.id;

                        // Check if member has active evaluation for current semester
                        // Only show as evaluated if they have meaningful evaluation data (OVIT evaluation or non-zero base score)
                        const memberEvaluations = !evaluationsLoading && allEvaluations.length > 0 ? 
                          allEvaluations.filter(evaluation => evaluation.guard_id === member.user_id) : [];
                        
                        const hasActiveEvaluation = memberEvaluations.length > 0 && 
                          memberEvaluations.some(evaluation => 
                            (evaluation.details?.has_ovit_evaluation) || 
                            (evaluation.details?.has_shiftask_evaluation) || 
                            (evaluation.base_score > 0)
                          );
                        
                        const backgroundClass = hasActiveEvaluation 
                          ? 'bg-green-100/80 dark:bg-green-900/30 border-green-200 dark:border-green-800 hover:bg-green-200/80 dark:hover:bg-green-900/50' 
                          : 'bg-red-100/80 dark:bg-red-900/30 border-red-200 dark:border-red-800 hover:bg-red-200/80 dark:hover:bg-red-900/50';

                       return (
                          <div
                            key={member.id}
                            onClick={() => handleEmployeeSelect(member)}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                              isSelected 
                                ? 'bg-primary/10 border-primary' 
                                : backgroundClass
                            }`}
                          >
                         <Avatar className="h-8 w-8">
                           <AvatarFallback className="text-xs bg-muted text-foreground">
                             {initials}
                           </AvatarFallback>
                         </Avatar>
                         <div className="flex-1">
                           <p className="font-medium text-sm text-foreground whitespace-normal">{fullName}</p>
                           <p className="text-xs text-muted-foreground">{t("personnel") || "Personnel"}</p>
                         </div>
                         <div className="flex items-center gap-1">
                           <Button
                             size="sm"
                             variant="ghost"
                             onClick={(e) => {
                               e.stopPropagation();
                               handleAddBehavior(member);
                             }}
                             className="h-7 w-7 p-0 hover:bg-muted"
                             title="Καταγραφή Συμπεριφοράς"
                           >
                             <UserPlus className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                           </Button>
                           
                            {(isAdmin || hasRole('manager')) && (
                              <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedEmployee(member);
                                      setShowShiftaskEvaluationFormModal(true);
                                    }}
                                    className="h-7 w-7 p-0 hover:bg-muted"
                                    title={t("shiftaskEvaluation") || "Shiftask Evaluation"}
                                  >
                                   <Award className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                                 </Button>
                                
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddClientEvaluation(member);
                                  }}
                                  className="h-7 w-7 p-0 hover:bg-muted"
                                  title={t("clientEvaluation") || "Client Evaluation"}
                                >
                                  <Star className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                                </Button>
                              </>
                            )}
                            
                            {isClient && !hasRole('manager') && !isAdmin && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddClientEvaluation(member);
                                  }}
                                  className="h-7 w-7 p-0"
                                  title={t("clientEvaluation") || "Client Evaluation"}
                                >
                                  <Star className="h-3 w-3" />
                                </Button>
                            )}
                         </div>
                       </div>
                     );
                   }).filter(Boolean)
                )}
              </div>
            </div>
            </div>
          </CardContent>
        </Card>


        {/* Employee Details Panel */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              {selectedEmployee 
                ? `${t("evaluations") || "Αξιολογήσεις"} - ${selectedEmployee.profiles.full_name || 
                   `${selectedEmployee.profiles.first_name} ${selectedEmployee.profiles.last_name}`}`
                : t("selectEmployee") || "Επιλέξτε Υπάλληλο"
              }
            </CardTitle>
            <CardDescription>
              {selectedEmployee 
                ? t("newBaseScoreSystemDetailed") || "Νέο Σύστημα Βασικού Σκορ - Αναλυτική προβολή απόδοσης"
                : t("selectEmployeeFromList") || "Επιλέξτε έναν υπάλληλο από τη λίστα για να δείτε τις αξιολογήσεις του"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedEmployee ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="h-16 w-16 mx-auto mb-4 opacity-20" />
                <p>{t("selectEmployeeToViewEvaluations") || "Επιλέξτε έναν υπάλληλο για να δείτε τις αξιολογήσεις του"}</p>
              </div>
            ) : evaluationsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : selectedEmployeeEvaluations.length === 0 ? (
              <div className="space-y-4">
                <div className="text-center py-4 text-muted-foreground">
                  <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t("usingNewEvaluationSystem") || "Using New Evaluation System"}</p>
                  <p className="text-xs">{t("baseScoreFromShiftask") || "Base score from Shiftask evaluation + performance"}</p>
                </div>
                
                <PerformanceBreakdownView 
                  guardId={selectedEmployee.user_id}
                  guardName={selectedEmployee.profiles.full_name || 
                    `${selectedEmployee.profiles.first_name} ${selectedEmployee.profiles.last_name}`}
                  teamId={selectedEmployee.team_id}
                />
              </div>
            ) : (
              <Tabs defaultValue="evaluations" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="evaluations">{t("evaluations") || "Αξιολογήσεις"}</TabsTrigger>
                  <TabsTrigger value="behaviors">{t("behaviors") || "Συμπεριφορές"}</TabsTrigger>
                </TabsList>

                <TabsContent value="evaluations" className="space-y-4">
                  {selectedEmployeeEvaluations.map((evaluation) => (
                    <div
                      key={evaluation.guard_id}
                      className="border border-border rounded-lg hover:bg-accent/20 dark:hover:bg-accent/10"
                    >
                      <div className="flex items-center justify-between p-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-4">
                            <div>
                              <p className="font-medium">
                                Αξιολόγηση: {evaluation.guard_name}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Νέο Σύστημα Βασικού Σκορ
                              </p>
                            </div>
                          </div>
                          <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">Τελικό Σκορ:</span>
                              <div className="font-medium text-lg">
                                {(evaluation.final_score || 0) > 100 ? '100+' : (evaluation.final_score || 0).toFixed(1)}
                              </div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Βαθμός:</span>
                              <div className="font-medium">
                                <Badge className={getGradeColor(evaluation.performance_grade || 'F')}>
                                  {(evaluation.final_score || 0) > 100 ? 'A+' : evaluation.performance_grade || 'F'}
                                </Badge>
                                {(evaluation.final_score || 0) > 100 && (
                                  <div className="mt-1">
                                    <DistinctionBadge score={evaluation.final_score || 0} size="sm" showIcon={false} />
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">επιχειρησιακά:</span>
                              <div className="font-medium text-blue-600">+{((evaluation.operational_activity_score || 0) + (evaluation.supervisor_reports_score || 0)).toFixed(1)}</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Συμπεριφορές:</span>
                              <div className={`font-medium ${(evaluation.behavior_points || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {(evaluation.behavior_points || 0) >= 0 ? '+' : ''}{(evaluation.behavior_points || 0).toFixed(1)}
                              </div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Συνέπεια:</span>
                              <div className={`font-medium ${(evaluation.punctuality_points || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {(evaluation.punctuality_points || 0) >= 0 ? '+' : ''}{(evaluation.punctuality_points || 0).toFixed(1)}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setExpandedEvaluationId(
                                expandedEvaluationId === evaluation.guard_id ? null : evaluation.guard_id
                              );
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Expanded details */}
                      {expandedEvaluationId === evaluation.guard_id && (
                        <div className="border-t border-border p-4 bg-muted/30">
                          <PerformanceBreakdownView
                            guardId={evaluation.guard_id}
                            guardName={
                              teamMembers.find(member => member.user_id === evaluation.guard_id)?.profiles.full_name ||
                              `${teamMembers.find(member => member.user_id === evaluation.guard_id)?.profiles.first_name} ${teamMembers.find(member => member.user_id === evaluation.guard_id)?.profiles.last_name}` ||
                              'Unknown Guard'
                            }
                            teamId={teamMembers.find(member => member.user_id === evaluation.guard_id)?.team_id}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="behaviors" className="space-y-4">
                  <BehaviorRecordsList 
                    guardId={selectedEmployee.user_id} 
                    canAddBehavior={isAdmin || hasRole('manager')}
                    onAddBehavior={() => {
                      setShowBehaviorModal(true);
                    }}
                  />
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <BehaviorTrackingModal
        open={showBehaviorModal}
        onOpenChange={setShowBehaviorModal}
        guardId={selectedEmployee?.user_id}
        guardName={selectedEmployee ? 
          selectedEmployee.profiles.full_name || 
          `${selectedEmployee.profiles.first_name} ${selectedEmployee.profiles.last_name}` 
          : undefined
        }
      />

      <ClientEvaluationModal
        open={showClientEvaluationModal}
        onOpenChange={setShowClientEvaluationModal}
        guardId={selectedEmployee?.user_id}
        guardName={selectedEmployee ? 
          selectedEmployee.profiles.full_name || 
          `${selectedEmployee.profiles.first_name} ${selectedEmployee.profiles.last_name}` 
          : undefined
        }
        guardAvatar={selectedEmployee?.profiles?.avatar_url}
        teamId={selectedEmployee?.team_id}
      />
      <ShiftaskEvaluationModal
        isOpen={showShiftaskEvaluationModal}
        onClose={() => setShowShiftaskEvaluationModal(false)}
        guardId={selectedEmployee?.user_id || ''}
        guardName={selectedEmployee ? 
          selectedEmployee.profiles.full_name || 
          `${selectedEmployee.profiles.first_name} ${selectedEmployee.profiles.last_name}` 
          : ''
        }
      />

      <ShiftaskEvaluationFormModal
        open={showShiftaskEvaluationFormModal}
        onOpenChange={setShowShiftaskEvaluationFormModal}
        guardId={selectedEmployee?.user_id}
        guardName={selectedEmployee ? 
          selectedEmployee.profiles.full_name || 
          `${selectedEmployee.profiles.first_name} ${selectedEmployee.profiles.last_name}` 
          : undefined
        }
      />
        </div>
  );
};